// Hub auth: session probe + login/logout. The JWT itself lives in an httpOnly cookie
// (set/cleared by the backend) — JS never sees it; ["me"] is the single source of truth
// for "am I logged in".

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./client";

export interface Me {
  username: string;
  demo?: boolean; // DEMO-group session: synthetic data, operator pages hidden
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => apiFetch<Me>("/api/auth/me"),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** A DEMO-group session: synthetic data, operator-only details (pipeline, capture mode) hidden. */
export function useIsDemo(): boolean {
  return Boolean(useMe().data?.demo);
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      apiFetch<Me>("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: (me) => {
      qc.setQueryData(["me"], me);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => apiFetch<void>("/api/auth/logout", { method: "POST" }),
    onSettled: () => {
      qc.clear(); // drop everything cached while authenticated
      navigate("/login", { replace: true });
    },
  });
}
